import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ScanLine, RefreshCw, AlertTriangle, Plus, X, Search, Loader2,
  Activity, Heart, Layers, CircleDot, Microscope, Scan,
  type LucideProps,
} from 'lucide-react';
import { format } from 'date-fns';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import { clsx } from 'clsx';

const URGENCY_STYLE: Record<string, string> = {
  routine:   'bg-gray-100 text-gray-600',
  urgent:    'bg-yellow-100 text-yellow-700',
  emergency: 'bg-red-100 text-red-600',
};

const STATUS_STYLE: Record<string, string> = {
  ordered:     'bg-gray-100 text-gray-600',
  scheduled:   'bg-blue-100 text-blue-700',
  in_progress: 'bg-orange-100 text-orange-600',
  reported:    'bg-green-100 text-green-700',
  cancelled:   'bg-red-100 text-red-500',
};

const MODALITY_ICON: Record<string, React.ComponentType<LucideProps>> = {
  xray:        ScanLine,
  usg:         Activity,
  ct:          Layers,
  mri:         CircleDot,
  ecg:         Activity,
  echo_2d:     Heart,
  dexa:        Scan,
  mammography: Microscope,
  other:       ScanLine,
};

const MODALITIES: { key: string; label: string; Icon: React.ComponentType<LucideProps> }[] = [
  { key: 'xray',        label: 'X-Ray',   Icon: ScanLine   },
  { key: 'usg',         label: 'USG',     Icon: Activity   },
  { key: 'ct',          label: 'CT Scan', Icon: Layers     },
  { key: 'mri',         label: 'MRI',     Icon: CircleDot  },
  { key: 'ecg',         label: 'ECG',     Icon: Activity   },
  { key: 'echo_2d',     label: 'ECHO 2D', Icon: Heart      },
  { key: 'dexa',        label: 'DEXA',    Icon: Scan       },
  { key: 'mammography', label: 'Mammo',   Icon: Microscope },
  { key: 'other',       label: 'Other',   Icon: ScanLine   },
];

const BODY_PART_SUGGESTIONS = [
  'Chest', 'Abdomen', 'Pelvis', 'Whole Abdomen', 'KUB',
  'Spine (L/S)', 'Spine (C/S)', 'Spine (T/S)',
  'Knee', 'Hip', 'Shoulder', 'Wrist', 'Ankle', 'Elbow',
  'Brain', 'Neck', 'Skull', 'Sinuses',
  'Obstetric (Fetal)',
];

// ── New Direct Order Modal ─────────────────────────────────────────
function NewRadiologyOrderModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [patientQ,  setPatientQ]  = useState('');
  const [patient,   setPatient]   = useState<any>(null);
  const [modality,  setModality]  = useState('');
  const [bodyPart,  setBodyPart]  = useState('');
  const [urgency,   setUrgency]   = useState<'routine' | 'urgent' | 'emergency'>('routine');
  const [history,   setHistory]   = useState('');
  const [isPcpndt,  setIsPcpndt]  = useState(false);

  const { data: pData, isFetching: pLoading } = useQuery({
    queryKey: ['rad-pt-search', patientQ],
    queryFn:  () => patientQ.length >= 2
      ? api.get('/lab/patients', { params: { search: patientQ } }).then(r => r.data)
      : Promise.resolve({ patients: [] }),
    staleTime: 0,
  });
  const patients: any[] = pData?.patients ?? [];

  const createMut = useMutation({
    mutationFn: (payload: object) => api.post('/radiology/orders', payload),
    onSuccess: () => { onCreated(); onClose(); },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!patient || !modality || !bodyPart.trim()) return;
    createMut.mutate({
      patient_id:       patient.id,
      modality,
      body_part:        bodyPart.trim(),
      urgency,
      clinical_history: history.trim() || undefined,
      is_pcpndt:        isPcpndt,
    });
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-navy">New Radiology Order</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20}/>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Patient */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Patient *</label>
            {patient ? (
              <div className="flex items-center justify-between p-3 bg-teal-50 rounded-xl border border-teal-200">
                <div>
                  <p className="font-semibold text-gray-800">{patient.name}</p>
                  <p className="text-xs text-gray-400">{patient.patient_uid} · {patient.gender}</p>
                </div>
                <button type="button" onClick={() => setPatient(null)} className="text-gray-400 hover:text-red-500">
                  <X size={16}/>
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                <input
                  value={patientQ}
                  onChange={e => setPatientQ(e.target.value)}
                  placeholder="Search by name or UID…"
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-navy"
                />
                {pLoading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin"/>}
                {patients.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-48 overflow-y-auto">
                    {patients.map((p: any) => (
                      <button key={p.id} type="button"
                        onMouseDown={() => { setPatient(p); setPatientQ(''); }}
                        className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm border-b border-gray-50 last:border-0">
                        <span className="font-medium text-gray-800">{p.name}</span>
                        <span className="ml-2 text-xs text-gray-400">{p.patient_uid} · {p.gender}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Modality */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Modality *</label>
            <div className="grid grid-cols-3 gap-2">
              {MODALITIES.map(m => (
                <button key={m.key} type="button" onClick={() => setModality(m.key)}
                  className={clsx(
                    'flex flex-col items-center gap-1 py-2.5 rounded-xl border text-xs font-medium transition-all',
                    modality === m.key
                      ? 'border-navy bg-navy/5 text-navy'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  )}>
                  <m.Icon size={16}/>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Body Part */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Body Part / Region *</label>
            <input
              value={bodyPart}
              onChange={e => setBodyPart(e.target.value)}
              placeholder="e.g. Chest, Abdomen, Knee…"
              list="rad-body-parts"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-navy"
            />
            <datalist id="rad-body-parts">
              {BODY_PART_SUGGESTIONS.map(s => <option key={s} value={s}/>)}
            </datalist>
          </div>

          {/* Urgency */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Urgency</label>
            <select value={urgency} onChange={e => setUrgency(e.target.value as any)}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-navy">
              <option value="routine">Routine</option>
              <option value="urgent">Urgent</option>
              <option value="emergency">Emergency</option>
            </select>
          </div>

          {/* Clinical History */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Clinical History</label>
            <textarea value={history} onChange={e => setHistory(e.target.value)}
              placeholder="Brief clinical notes for the radiologist…"
              rows={3}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-navy resize-none"/>
          </div>

          {/* PCPNDT — only for USG */}
          {modality === 'usg' && (
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input type="checkbox" checked={isPcpndt} onChange={e => setIsPcpndt(e.target.checked)} className="rounded"/>
              <span className="font-medium text-red-600">PCPNDT — Obstetric / Foetal USG (PNDT Act)</span>
            </label>
          )}

          {createMut.isError && (
            <p className="text-red-500 text-sm">Failed to create order. Please try again.</p>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit"
              disabled={!patient || !modality || !bodyPart.trim() || createMut.isPending}
              className="flex-1 py-2.5 rounded-xl bg-navy text-white text-sm font-semibold disabled:opacity-50 hover:bg-navy/90 flex items-center justify-center gap-2">
              {createMut.isPending
                ? <><Loader2 size={14} className="animate-spin"/> Creating…</>
                : 'Create Order'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ── Main Page ──────────────────────────────────────────────────────
export default function RadiologyPage() {
  const today        = format(new Date(), 'yyyy-MM-dd');
  const user         = useAuthStore(s => s.user);
  const qc           = useQueryClient();
  const [modality,      setModality]      = useState('');
  const [urgency,       setUrgency]       = useState('');
  const [newOrderOpen,  setNewOrderOpen]  = useState(false);

  const canOrder = ['hospital_admin', 'owner', 'radiologist', 'doctor'].includes(user?.role ?? '');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['radiology-orders', modality, urgency, today],
    queryFn:  () => api.get('/radiology/orders', {
      params: { modality: modality || undefined, urgency: urgency || undefined, date: today }
    }).then(r => r.data),
    refetchInterval: 60_000,
  });

  const { data: stats } = useQuery({
    queryKey: ['radiology-stats'],
    queryFn:  () => api.get('/radiology/stats').then(r => r.data),
  });

  const orders = data?.orders ?? [];

  return (
    <div className="space-y-6 p-6 max-w-screen-xl mx-auto">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-navy">Radiology (RIS)</h1>
          <p className="text-sm text-gray-400 mt-0.5">Imaging worklist · {today}</p>
        </div>
        <div className="flex gap-3">
          {canOrder && (
            <button onClick={() => setNewOrderOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy/90">
              <Plus size={16}/> New Direct Order
            </button>
          )}
          <button onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''}/>
            Refresh
          </button>
        </div>
      </div>

      {stats && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-card flex gap-4 text-sm flex-wrap">
          <span className="text-gray-400">Pending today:</span>
          <span className="font-semibold text-navy">{stats.total_pending ?? 0}</span>
          {(stats.today_by_modality ?? []).map((m: any) => (
            <span key={`${m.modality}-${m.status}`} className="text-gray-500">
              {(() => { const I = MODALITY_ICON[m.modality] ?? ScanLine; return <I size={12} className="inline-block align-middle mr-0.5"/>; })()} {m.modality.toUpperCase()}: <strong>{m.cnt}</strong> {m.status}
            </span>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select value={modality} onChange={e => setModality(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy">
          <option value="">All Modalities</option>
          {['xray','usg','ct','mri','ecg','echo_2d','dexa','mammography','other'].map(m => (
            <option key={m} value={m}>{m.toUpperCase()}</option>
          ))}
        </select>
        <select value={urgency} onChange={e => setUrgency(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-navy">
          <option value="">All Urgency</option>
          <option value="emergency">Emergency</option>
          <option value="urgent">Urgent</option>
          <option value="routine">Routine</option>
        </select>
      </div>

      {/* Worklist */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Modality</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Patient</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Body Part</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Urgency</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Ordered By</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Time</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {isLoading ? (
              <tr><td colSpan={7} className="text-center py-12 text-gray-400">Loading…</td></tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-12">
                  <ScanLine size={36} className="mx-auto text-gray-300 mb-3"/>
                  <p className="text-gray-400 text-sm">No imaging orders today</p>
                  {canOrder && (
                    <button onClick={() => setNewOrderOpen(true)}
                      className="mt-3 px-4 py-2 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy/90">
                      + New Direct Order
                    </button>
                  )}
                </td>
              </tr>
            ) : orders.map((order: any) => (
              <tr key={order.id} className="hover:bg-gray-50 cursor-pointer">
                <td className="px-4 py-3">
                  {(() => { const I = MODALITY_ICON[order.modality] ?? ScanLine; return <I size={16} className="inline-block text-navy/60"/>; })()}
                  <span className="text-xs text-gray-600 ml-1.5">{order.modality.toUpperCase()}</span>
                  {order.is_pcpndt && (
                    <span className="ml-1.5 px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded font-medium">PCPNDT</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-800">{order.patient_uid}</div>
                  <div className="text-xs text-gray-400">{order.patient_name_enc}</div>
                </td>
                <td className="px-4 py-3 text-gray-600 text-xs">{order.body_part}</td>
                <td className="px-4 py-3">
                  <span className={clsx('px-2 py-1 rounded-lg text-xs font-medium', URGENCY_STYLE[order.urgency])}>
                    {order.urgency}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={clsx('px-2 py-1 rounded-lg text-xs font-medium', STATUS_STYLE[order.status])}>
                    {order.status.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{order.ordered_by_name}</td>
                <td className="px-4 py-3 text-xs text-gray-400">
                  {new Date(order.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {newOrderOpen && (
        <NewRadiologyOrderModal
          onClose={() => setNewOrderOpen(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ['radiology-orders'] });
            qc.invalidateQueries({ queryKey: ['radiology-stats'] });
          }}
        />
      )}
    </div>
  );
}
